import {
  QuickSearchIcon,
  DeepSearchIcon,
  ProSearchIcon,
  MeterIcon,
  Text,
} from "design_system";
import { ReactNode } from "react";

/**
 * Supported Co Feature types - supports both long and short forms
 */
export type CoFeatureType = "quick" | "pro" | "deep" | "consensus_meter";

/**
 * Feature configuration with icon and display text
 */
const FEATURE_CONFIG: Record<
  CoFeatureType,
  { icon: ReactNode; display_text: string }
> = {
  quick: {
    icon: <QuickSearchIcon />,
    display_text: "Summary",
  },
  pro: {
    icon: <ProSearchIcon />,
    display_text: "Pro Analysis",
  },
  deep: {
    icon: <DeepSearchIcon />,
    display_text: "Deep Search",
  },
  consensus_meter: {
    icon: <MeterIcon />,
    display_text: "Consensus Meter",
  },
};

/**
 * Props for the CoFeature component
 */
type CoFeatureProps = {
  /** The type of Co Feature to display */
  feature: CoFeatureType;
};

/**
 * A component that displays Co Features (Summary, Pro Analysis, Deep Search, Consensus Meter)
 * with icons and bold text, similar to SearchModeSelector styling.
 *
 * Features:
 * - Static icons for all features
 * - Bold text display
 */
function CoFeature({ feature }: CoFeatureProps) {
  const config = FEATURE_CONFIG[feature];

  return (
    <span className="flex items-center justify-start gap-3">
      {config.icon}
      <Text size="sm" weight="medium" className="m-0!">
        {config.display_text}
      </Text>
    </span>
  );
}

/**
 * Placeholder component shown while CoFeature data is loading
 */
export const CoFeaturePlaceholder = () => {
  return (
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 bg-bg-subtle animate-pulse rounded" />
      <div className="bg-bg-subtle animate-pulse h-2.5 rounded w-24" />
    </div>
  );
};

/**
 * Converts CoFeature data to markdown format for plain text export
 */
export const CoFeatureMarkdown = ({ feature }: CoFeatureProps) => {
  return `**${FEATURE_CONFIG[feature].display_text}**`;
};

export default CoFeature;
